import { FC, useCallback, useEffect, useMemo, useState } from "react";
import { getSections } from "../../lib/const/sections";
import { getNavIcon } from "../../lib/const/icons";
import { Link, useLocation } from "react-router-dom";
import { ISection } from "../../model/types";
import classes from './navPages.module.scss'
import { useAppSelector } from "../../../../app/store/store";
import { EVENTS_ROUTE, MY_ROUTE, STAFF_ROUTE } from "../../../../app/router/routes";
import { FEATURE_SECTIONS, hasFeature } from "../../../../entities/my";
import { eventsService, TLessonEventReviewLevel } from "../../../../entities/events";

interface IProps {
    onReplacePath?: () => void;
}

const getReviewLevelByRole = (role: string): TLessonEventReviewLevel => {
    switch(role){
        case 'coordinator':
            return 'LESSON_EVENT_REVIEW_LEVEL_COORDINATOR'
        case 'assistant':
            return 'LESSON_EVENT_REVIEW_LEVEL_HEAD_TEACHER'
        case 'admin':
        case 'super_admin':
            return 'LESSON_EVENT_REVIEW_LEVEL_ADMIN'
        default:
            return 'LESSON_EVENT_REVIEW_LEVEL_UNSPECIFIED'
    }
}

const getStateFilterKey = (level: TLessonEventReviewLevel): 'coordinatorStates' | 'headTeacherStates' | 'adminStates' => {
    switch(level){
        case 'LESSON_EVENT_REVIEW_LEVEL_COORDINATOR':
            return 'coordinatorStates'
        case 'LESSON_EVENT_REVIEW_LEVEL_HEAD_TEACHER':
            return 'headTeacherStates'
        case 'LESSON_EVENT_REVIEW_LEVEL_ADMIN':
            return 'adminStates'
        default:
            return 'coordinatorStates'
    }
}

export const NavPages: FC<IProps> = ({onReplacePath}) => {
    const {my} = useAppSelector(s => s.myReducer)
    const [pendingEventsCount, setPendingEventsCount] = useState<number>(0)
    const links: ISection[] = hasFeature(my, FEATURE_SECTIONS.HOME)
        ? [{title: 'Главная', link: '/', group: 'main'}]
        : []
    const sections = getSections(my)
    const reviewLevel = useMemo(() => getReviewLevelByRole(my.role), [my.role])
    const canLoadEventsCounter = hasFeature(my, FEATURE_SECTIONS.EVENTS)
        && reviewLevel !== 'LESSON_EVENT_REVIEW_LEVEL_UNSPECIFIED'

    sections.forEach(s => links.push(s))

    const {pathname} = useLocation()
    const profileName = my.full_name.trim() || 'Пользователь'
    const profileLetter = profileName.charAt(0).toUpperCase()
    const canOpenProfile = hasFeature(my, FEATURE_SECTIONS.PROFILE)

    const getSelected = useCallback((link: string) => {
        if(link === '/'){
            return pathname === '/'
        }

        const normalizedLink = link.endsWith('/') ? link.slice(0, -1) : link
        return pathname === normalizedLink || pathname.startsWith(`${normalizedLink}/`)
    }, [pathname])

    const mainLinks = links.filter(link => link.group !== 'bottom')
    const bottomLinks = links.filter(link => link.group === 'bottom')

    const loadPendingEventsCount = useCallback(async () => {
        if(!canLoadEventsCounter){
            setPendingEventsCount(0)
            return
        }

        try{
            const stateFilterKey = getStateFilterKey(reviewLevel)
            const response = await eventsService.listLessonEvents({
                filter: {
                    [stateFilterKey]: ['LESSON_EVENT_REVIEW_STATE_PENDING'],
                    onlyAssignedToMe: reviewLevel !== 'LESSON_EVENT_REVIEW_LEVEL_COORDINATOR',
                },
                limit: 1,
                offset: 0,
            })
            setPendingEventsCount(Number(response.total) || 0)
        }
        catch(e){
            setPendingEventsCount(0)
        }
    }, [canLoadEventsCounter, reviewLevel])

    useEffect(() => {
        loadPendingEventsCount()
        const interval = window.setInterval(loadPendingEventsCount, 60 * 1000)
        return () => window.clearInterval(interval)
    }, [loadPendingEventsCount, pathname])


    return (
        <section className={classes.navPages}>
            <ul className={classes.list}>
                {mainLinks.map(link => 
                    <li 
                        className={classes.link + (getSelected(link.link) ? ` ${classes.selected}` : '')} 
                        key={`${link.title}-${link.link}`}
                    >
                        <Link
                            to={{pathname: link.link, search: ''}}
                            state={link.link === STAFF_ROUTE.path ? {resetFilters: true} : undefined}
                            onClick={onReplacePath}
                        >
                            <section className={classes.icon}>{getNavIcon(link.link)}</section>
                            <span>{link.title}</span>
                            {link.link === EVENTS_ROUTE.path && pendingEventsCount > 0 && (
                                <strong className={classes.counter}>{pendingEventsCount > 99 ? '99+' : pendingEventsCount}</strong>
                            )}
                        </Link>
                    </li>
                )}
            </ul>
            {
                bottomLinks.length > 0 
                    &&
                <section className={classes.bottomBlock}>
                    <ul className={classes.list}>
                        {bottomLinks.map(link => 
                            <li 
                                className={classes.link + (getSelected(link.link) ? ` ${classes.selected}` : '')} 
                                key={`${link.title}-${link.link}`}
                            >
                                <Link
                                    to={{pathname: link.link, search: ''}}
                                    state={link.link === STAFF_ROUTE.path ? {resetFilters: true} : undefined}
                                    onClick={onReplacePath}
                                >
                                    <section className={classes.icon}>{getNavIcon(link.link)}</section>
                                    <span>{link.title}</span>
                                    {link.link === EVENTS_ROUTE.path && pendingEventsCount > 0 && (
                                        <strong className={classes.counter}>{pendingEventsCount > 99 ? '99+' : pendingEventsCount}</strong>
                                    )}
                                </Link>
                            </li>
                        )}
                    </ul>
                </section>
            }
            {   
                canOpenProfile 
                    && 
                <Link 
                    to={{pathname: MY_ROUTE.path, search: ''}} 
                    className={classes.profile + (pathname === MY_ROUTE.path ? ` ${classes.profileSelected}` : '')}
                    onClick={onReplacePath}
                >
                    <section className={classes.avatar}>{profileLetter}</section>
                    <section className={classes.profileInfo}>
                        <section className={classes.profileName}>{profileName}</section>
                    </section>
                </Link>
            }
        </section>
    )
}
